import './new-form-text-field.scss';
import 'react-phone-number-input/style.css';

import { Box, Grid, Skeleton, TextField, TextFieldProps } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormRichTextField,
  FormRichTextFieldProps,
} from './form-rich-text-field';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { BaseSyntheticEvent, useMemo } from 'react';
import { FormFieldContainer } from './form-field-container';
import { NewConvergeInputLabel } from './new-converge-input-label';
import PhoneInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import React from 'react';

const formatPhoneNumber = (input: string) => {
  // Remove all non-numeric characters
  const cleaned = input.replace(/\D/g, '');
  // Format the cleaned string as xxx-xxx-xxxx
  const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
  if (match) {
    return [match[1], match[2], match[3]].filter(Boolean).join('-');
  }
  return input;
};

const MemoizedPhoneInput = React.memo(PhoneInput, (prevProps, nextProps) => {
  return (
    prevProps.value === nextProps.value &&
    prevProps.disabled === nextProps.disabled
  );
});

export type NewFormTextFieldProps =
  | ({
      name: string;
      placeholderText?: string;
      containerWidth?: number;
      sideBySide?: boolean;
      defaultValue?: string | null;
      autoComplete?: boolean;
      hasWidth?: string;
      isRichText?: boolean;
      isCheckBox?: boolean;
      width?: string;
      isNumber?: boolean;
      isPhoneNumber?: boolean;
      tooltip?: string;
      isLoading?: boolean;
      hideMenu?: boolean;
      hideBorders?: boolean;
      disableHoverBorder?: boolean;
      dataTestId?: string;
      onBlur?: (e?: BaseSyntheticEvent<object, any, any> | undefined) => void;
    } & Omit<TextFieldProps, 'autoComplete' | 'onBlur'>)
  | (Omit<FormRichTextFieldProps, 'autoComplete' | 'onBlur'> & {
      sideBySide?: boolean;
      autoComplete?: boolean;
      defaultValue?: string | null;
      isPrimaryEndorsement?: boolean;
      placeholderText?: string;
      isCheckBox?: boolean;
      isNumber?: boolean;
      isPhoneNumber?: boolean;
      tooltip?: string;
      width?: string;
      isLoading?: boolean;
      hideMenu?: boolean;
      hideBorders?: boolean;
      disableHoverBorder?: boolean;
      onBlur?: (e?: BaseSyntheticEvent<object, any, any> | undefined) => void;
      dataTestId?: string;
    });

export const NewFormTextField = ({
  name,
  label,
  required,
  containerWidth = 6,
  sideBySide,
  autoComplete,
  onBlur,
  hideMenu,
  defaultValue,
  placeholderText,
  isCheckBox,
  width,
  value,
  isNumber = false,
  isPhoneNumber = false,
  className,
  tooltip,
  disableHoverBorder = false,
  disabled,
  isLoading = false,
  dataTestId,
  ...otherProps
}: NewFormTextFieldProps) => {
  const { control } = useFormContext();

  const phoneInputStyle = useMemo(
    () => ({
      '& input': {
        color: 'green',
        fontSize: '14px',
        padding: '40px',
      },
    }),
    []
  );

  if ('isRichText' in otherProps && otherProps.isRichText === true) {
    return (
      <FormRichTextField
        name={name}
        hideMenu={hideMenu}
        label={label}
        value={value}
        required={required}
        containerWidth={containerWidth}
        defaultValue={defaultValue}
        onBlur={() => onBlur && onBlur()}
        {...otherProps}
      />
    );
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState: { error } }) => {
        const labelComponent = otherProps.hiddenLabel !== true && (
          <NewConvergeInputLabel
            label={label}
            required={required}
            className={error?.message ? 'error-message' : className}
            tooltip={tooltip}
          />
        );

        const fieldComponent = isLoading ? (
          <Skeleton
            variant="rectangular"
            height={36}
            className="new-form-text-field-loading"
            sx={{
              borderRadius: '3px',
              backgroundColor: '#f5f5f5',
            }}
          />
        ) : !isPhoneNumber ? (
          <TextField
            className={`new-form-text-field ${className || ''} ${
              disableHoverBorder ? 'no-hover-border' : ''
            }`}
            placeholder={placeholderText}
            {...field}
            onChange={(e) => {
              if (isPhoneNumber) {
                // Store only numbers for phone numbers to prevent formatting conflicts
                const numericOnly = e.target.value.replace(/\D/g, '');
                field.onChange(numericOnly);
              } else {
                field.onChange(e.target.value);
              }
            }}
            value={
              isPhoneNumber && field.value
                ? formatPhoneNumber(field.value)
                : field.value ?? value ?? ''
            }
            defaultValue={defaultValue}
            disabled={disabled}
            {...otherProps}
            sx={{
              '& .MuiFormHelperText-root.Mui-error': {
                marginLeft: '0px',
                fontWeight: 600,
              },
              '& input::placeholder': {
                opacity: 1,
                fontWeight: 600,
                color: 'rgba(0, 0, 0, 0.6)',
              },
              ...(disableHoverBorder && {
                // Set gray border and text color for disabled state
                '& .MuiOutlinedInput-root': {
                  color: '#808080 !important', // Gray text color
                },
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#b9b9b9ff !important', // Gray border
                },
                '& .MuiOutlinedInput-root input': {
                  color: '#808080 !important', // Gray input text color
                },
                '& .MuiOutlinedInput-root textarea': {
                  color: '#808080 !important', // Gray textarea text color
                },
                // Disable hover effect with gray styling
                '& .MuiOutlinedInput-root:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline':
                  {
                    borderColor: '#b9b9b9ff !important',
                  },
                // Disable focus effect with gray styling
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                  {
                    border: '1px solid #b9b9b9ff !important',
                    borderWidth: '1px !important',
                  },
              }),
            }}
            inputProps={{
              defaultValue: defaultValue ?? undefined,
              autoComplete:
                typeof autoComplete == 'boolean'
                  ? autoComplete
                    ? 'on'
                    : 'off'
                  : undefined,
              inputMode: isNumber ? 'numeric' : undefined,
              pattern: isNumber ? '[0-9]*' : undefined,
              ...(dataTestId && { 'data-testid': dataTestId }),
            }}
            variant="outlined"
            size="small"
            fullWidth
            onInput={(e) => {
              if (isNumber && !isPhoneNumber) {
                const input = e.target as HTMLInputElement;
                input.value = input.value.replace(/[^0-9]/g, ''); // Remove any non-numeric characters
              }
            }}
            error={!!error}
            onBlur={(e) => {
              field.onBlur();
              onBlur && onBlur(e);
            }}
            helperText={
              error ? (
                <Box width={width ?? '200px'} whiteSpace="nowrap">
                  {(error.message as string) || 'This field is required'}
                </Box>
              ) : (
                ''
              )
            }
          />
        ) : (
          <MemoizedPhoneInput
            flags={flags}
            international
            countryCallingCodeEditable={false}
            style={phoneInputStyle}
            defaultCountry={'US'}
            placeholder="415 555 1212"
            value={field.value}
            onChange={field.onChange}
            disabled={disabled}
            inputProps={dataTestId ? { 'data-testid': dataTestId } : undefined}
          />
        );

        return (
          <FormFieldContainer
            md={containerWidth}
            className={isPhoneNumber ? className : ''}
          >
            {sideBySide ? (
              <Grid
                container
                flexDirection={'row'}
                flexGrow={1}
                flexBasis={1}
                direction={'row'}
                spacing={2}
              >
                <Grid item xs={6}>
                  {labelComponent}
                </Grid>
                <Grid item xs={6}>
                  {fieldComponent}
                </Grid>
              </Grid>
            ) : (
              <>
                {labelComponent}
                {fieldComponent}
              </>
            )}
          </FormFieldContainer>
        );
      }}
    />
  );
};
